import {
  AUDIT_ACTION_LABELS,
  AUDIT_SOURCE_LABELS,
  AUDIT_SOURCES,
  auditOptionsSchema,
  auditPageSchema,
  formatDateTime,
  type AuditEntry,
} from '@fluxus/shared';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { History, X } from 'lucide-react';
import { useId } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { useAuth } from '@/lib/auth/session';

/** O que o histórico de um KPI traz, pela entidade filtrada ("" = tudo do KPI). */
const KPI_SCOPE_PARTS: Record<string, string> = {
  '': 'cadastro, metas, resultados e alertas',
  kpi: 'cadastro',
  kpi_target: 'metas',
  kpi_result: 'resultados',
  alert: 'alertas',
};

/**
 * Configurações › Auditoria e histórico (T-202b, RF-46, RN-16, PRD §18; DEC-067): a trilha da empresa por mudança,
 * com quem, quando, a origem, o anterior → novo e a justificativa; filtros por usuário, entidade, registro (pelo
 * atalho das telas de KPI, meta, objetivo e decisão), período e origem.
 */
export function AuditoriaPage() {
  const { organizationId = '' } = useParams();
  const { client } = useAuth();
  const [params, setParams] = useSearchParams();
  const ids = { user: useId(), entity: useId(), source: useId(), from: useId(), to: useId() };
  const filters = {
    userId: params.get('usuario') ?? '',
    entity: params.get('entidade') ?? '',
    entityId: params.get('registro') ?? '',
    kpiId: params.get('kpi') ?? '',
    source: params.get('origem') ?? '',
    from: params.get('de') ?? '',
    to: params.get('ate') ?? '',
  };
  const setFilter = (key: string, value: string) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  const base = `/organizations/${organizationId}/audit`;
  const options = useQuery({
    queryKey: ['audit', organizationId, 'options'],
    queryFn: ({ signal }) => client.request(`${base}/options`, { schema: auditOptionsSchema, signal }),
  });
  const badRange = filters.from && filters.to && filters.from > filters.to;
  const trail = useInfiniteQuery({
    queryKey: ['audit', organizationId, filters],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      client.request(base, {
        query: {
          ...(filters.userId ? { userId: filters.userId } : {}),
          ...(filters.entity ? { entity: filters.entity } : {}),
          ...(filters.entityId ? { entityId: filters.entityId } : {}),
          ...(filters.kpiId ? { kpiId: filters.kpiId } : {}),
          ...(filters.source ? { source: filters.source } : {}),
          ...(filters.from ? { from: filters.from } : {}),
          ...(filters.to ? { to: filters.to } : {}),
          ...(pageParam ? { before: pageParam } : {}),
        },
        schema: auditPageSchema,
        signal,
      }),
    getNextPageParam: (last) => last.nextCursor,
    enabled: !badRange,
  });
  const items = trail.data?.pages.flatMap((p) => p.items) ?? [];
  const scoped = filters.entityId || filters.kpiId;
  // O nome vem do atalho (não depende da lista carregada); sem ele, o do primeiro item.
  const scopedName = params.get('nome') || items[0]?.entityName;
  // Histórico do KPI: o que a lista traz conforme a entidade filtrada (DEC-067).
  const kpiPart = filters.kpiId ? (KPI_SCOPE_PARTS[filters.entity] ?? KPI_SCOPE_PARTS['']) : null;
  return (
    <PageContainer className="max-w-5xl">
      <PageHeader
        title="Auditoria e histórico"
        description="Quem alterou o quê, quando, de onde, o valor anterior e o novo e a justificativa, em toda a empresa."
      />
      <section aria-label="Filtros da auditoria" className="mt-4 flex flex-col gap-3">
        {scoped ? (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
            <span>
              Histórico de {scopedName ?? (filters.kpiId ? 'um indicador' : 'um registro')}
              {kpiPart ? ` (${kpiPart})` : ''}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setParams(
                  (p) => {
                    // Toda a trilha: sai o registro, o KPI e a entidade do atalho (ficam pessoa, origem e período).
                    const next = new URLSearchParams(p);
                    for (const key of ['registro', 'kpi', 'entidade', 'nome']) next.delete(key);
                    return next;
                  },
                  { replace: true },
                )
              }
            >
              <X aria-hidden />
              Ver toda a trilha
            </Button>
          </div>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <FormField id={ids.user} label="Quem alterou">
            <NativeSelect
              id={ids.user}
              value={filters.userId}
              disabled={!options.data}
              onChange={(e) => setFilter('usuario', e.target.value)}
            >
              <option value="">Todos</option>
              {options.data?.users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </NativeSelect>
          </FormField>
          <FormField id={ids.entity} label="Entidade">
            <NativeSelect
              id={ids.entity}
              value={filters.entity}
              disabled={!options.data}
              onChange={(e) => setFilter('entidade', e.target.value)}
            >
              <option value="">Todas</option>
              {options.data?.entities.map((e) => (
                <option key={e.key} value={e.key}>
                  {e.label}
                </option>
              ))}
            </NativeSelect>
          </FormField>
          <FormField id={ids.source} label="Origem">
            <NativeSelect
              id={ids.source}
              value={filters.source}
              onChange={(e) => setFilter('origem', e.target.value)}
            >
              <option value="">Todas</option>
              {AUDIT_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {AUDIT_SOURCE_LABELS[s]}
                </option>
              ))}
            </NativeSelect>
          </FormField>
          <FormField id={ids.from} label="De" error={badRange ? 'O fim vem depois do início.' : undefined}>
            <Input
              id={ids.from}
              type="date"
              value={filters.from}
              onChange={(e) => setFilter('de', e.target.value)}
              className="h-11"
            />
          </FormField>
          <FormField id={ids.to} label="Até">
            <Input
              id={ids.to}
              type="date"
              value={filters.to}
              onChange={(e) => setFilter('ate', e.target.value)}
              className="h-11"
            />
          </FormField>
        </div>
        {options.isError ? (
          <ErrorState
            error={options.error}
            onRetry={() => void options.refetch()}
            retrying={options.isFetching}
          />
        ) : null}
      </section>

      <section aria-label="Trilha de auditoria" className="mt-6">
        {badRange ? null : trail.isPending ? (
          <LoadingState rows={4} label="Carregando a trilha" />
        ) : trail.isError ? (
          <ErrorState error={trail.error} onRetry={() => void trail.refetch()} retrying={trail.isFetching} />
        ) : !items.length ? (
          <EmptyState
            className="rounded-lg border border-dashed border-border py-12"
            icon={History}
            title="Nenhuma alteração encontrada"
            description="Mude os filtros ou o período para ver outras alterações."
          />
        ) : (
          <>
            <ol aria-label="Alterações, mais recentes primeiro" className="flex flex-col gap-3">
              {items.map((e) => (
                <li key={e.changeId}>
                  <Change entry={e} />
                </li>
              ))}
            </ol>
            {trail.hasNextPage ? (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="outline"
                  loading={trail.isFetchingNextPage}
                  onClick={() => void trail.fetchNextPage()}
                >
                  Carregar mais
                </Button>
              </div>
            ) : null}
          </>
        )}
      </section>
    </PageContainer>
  );
}

function Change({ entry: e }: { entry: AuditEntry }) {
  return (
    <article
      aria-label={`${e.entityLabel}${e.entityName ? `: ${e.entityName}` : ''} — ${formatDateTime(e.occurredAt)}`}
      className="rounded-lg border border-border p-4 text-sm"
    >
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <p className="font-semibold">
            {e.entityLabel}
            {e.entityName ? `: ${e.entityName}` : ''}
          </p>
          {e.detail ? <p className="text-sm">{e.detail}</p> : null}
          <p className="text-xs text-muted-foreground">
            {AUDIT_ACTION_LABELS[e.action]} · {AUDIT_SOURCE_LABELS[e.source]}
          </p>
        </div>
        <p className="text-xs text-muted-foreground">
          {formatDateTime(e.occurredAt)} · {e.actorName ?? 'Sistema'}
        </p>
      </header>
      {e.fields.length ? (
        <dl className="mt-3 grid gap-x-4 gap-y-1 sm:grid-cols-[minmax(8rem,auto)_1fr]">
          {e.fields.map((f) => (
            <div key={f.field} className="contents">
              <dt className="text-muted-foreground">{f.label}</dt>
              <dd className="break-words">
                {e.action === 'CREATE' ? (
                  f.after
                ) : (
                  <>
                    <span className="text-muted-foreground line-through decoration-1">{f.before}</span>
                    <span aria-hidden> → </span>
                    <span className="sr-only"> passou a </span>
                    <span className="font-medium">{f.after}</span>
                  </>
                )}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
      {e.reason ? (
        <p className="mt-3 text-sm">
          <span className="text-muted-foreground">Motivo: </span>
          {e.reason}
        </p>
      ) : null}
    </article>
  );
}
